import {
  Avatar,
  Box,
  Button,
  Center,
  Flex,
  IconButton,
  Image,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Modal,
  ModalBody,
  ModalContent,
  ModalOverlay,
  Text,
  useClipboard,
  useDisclosure,
} from '@chakra-ui/react'
import moment from 'moment'
import { useContext, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { BsThreeDotsVertical } from 'react-icons/bs'
import { FiChevronLeft, FiChevronRight } from 'react-icons/fi'
import { HiOutlineDocumentDownload } from 'react-icons/hi'
import { IoIosClose } from 'react-icons/io'
import { MdContentCopy } from 'react-icons/md'
import { RiDeleteBinFill } from 'react-icons/ri'
import useFetch, { CachePolicies } from 'use-http'
import { PermissionsEnum } from '../../../../utils'
import { isRestrictedUser } from '../../../../utils/roles'
import { removeTrailingSlash } from '../../../../utils/urlHelpers'
import { useQueryState } from '../../../hooks/useQueryState'
import useToast from '../../../hooks/useToast'
import {
  GenerateImageErrorResponse,
  GenerateImageResponse,
} from '../../../interfaces'
import { UserContext } from '../../../providers'
import PermissionGuard from '../../PermissionGuard'
import SaveNewFileModal from '../PhotoEditor/SaveNewFileModal'
import DeleteGeneratedImageModal from './DeleteGeneratedImageModal'

interface ExpandImageModalProps {
  isOpen: boolean
  onClose: () => void
  generatedImages: GenerateImageResponse[]
  currentIndex: number
  setCurrentIndex: React.Dispatch<React.SetStateAction<number>>
  refetch?: () => void
}

const ExpandImageModal = ({
  isOpen,
  onClose,
  generatedImages,
  currentIndex,
  setCurrentIndex,
  refetch,
}: ExpandImageModalProps) => {
  const { t } = useTranslation()
  const toast = useToast()
  const { user } = useContext(UserContext)
  const { request } = useFetch({
    cachePolicy: CachePolicies.NO_CACHE,
  })
  const {
    isOpen: isOpenDeleteGeneratedImageModal,
    onOpen: onOpenDeleteGeneratedImageModal,
    onClose: onCloseDeleteGeneratedImageModal,
  } = useDisclosure()
  const {
    isOpen: isOpenSaveFileModal,
    onOpen: onOpenSaveFileModal,
    onClose: onCloseSaveFileModal,
  } = useDisclosure()

  const [fetchedImage, setFetchedImage] =
    useState<GenerateImageResponse | null>(null)
  const [urlToCopy, setUrlToCopy] = useState<string>(
    generatedImages[currentIndex]?.url,
  )
  const { onCopy } = useClipboard(urlToCopy)

  const [selectedImageId, setSelectedImageId] = useQueryState<string | null>(
    null,
    'selectedImageId',
  )

  useEffect(() => {
    if (isOpen) {
      const selectedImageIndex = generatedImages.findIndex(
        image => image.imageId === selectedImageId,
      )

      if (selectedImageIndex === -1 && selectedImageId) {
        fetchImageById(selectedImageId)
      }
    }
  }, [isOpen, selectedImageId])

  const currentImage = useMemo(() => {
    return fetchedImage || generatedImages[currentIndex]
  }, [fetchedImage, generatedImages, currentIndex])

  useEffect(() => {
    if (isOpen) {
      setSelectedImageId(currentImage?.imageId)
      setUrlToCopy(currentImage?.url)
    }
  }, [currentImage, isOpen])

  const fetchImageById = async (imageId: string) => {
    const res = (await request.get(
      `/api/media-library/${user?.clientNumber}/generate-image/${imageId}`,
    )) as GenerateImageResponse | GenerateImageErrorResponse

    if (request.error) {
      toast({
        description: (res as GenerateImageErrorResponse).message,
        status: 'error',
        isClosable: true,
        duration: 5000,
        position: 'top-right',
        variant: 'outline',
      })
    } else {
      setFetchedImage(res as GenerateImageResponse)
      setUrlToCopy((res as GenerateImageResponse).url)
    }
  }

  const handleNextImage = () => {
    if (fetchedImage) {
      setFetchedImage(null)
    }
    setCurrentIndex(prevIndex =>
      prevIndex === generatedImages.length - 1 ? 0 : prevIndex + 1,
    )
  }

  const handlePreviousImage = () => {
    if (fetchedImage) {
      setFetchedImage(null)
    }
    setCurrentIndex(prevIndex =>
      prevIndex === 0 ? generatedImages.length - 1 : prevIndex - 1,
    )
  }

  const handleClose = () => {
    onClose()
    setSelectedImageId(null)
    setFetchedImage(null)
  }

  return (
    <>
      <Modal isOpen={isOpen} onClose={handleClose} size="xl" isCentered>
        <ModalOverlay />
        <ModalContent
          maxW="85vw"
          maxH="90vh"
          onKeyDown={event => {
            if (event.key === 'ArrowLeft') {
              handlePreviousImage()
            } else if (event.key === 'ArrowRight') {
              handleNextImage()
            }
          }}
        >
          <ModalBody display="flex" p="16px">
            <Flex width="100%" justifyContent="space-between">
              <Box
                borderRadius="8px"
                bg="appPaleGrey"
                border="1px solid #e2e8f0"
                width="70%"
                maxHeight="80vh"
                overflow="hidden"
                display="flex"
                alignItems="center"
                justifyContent="center"
              >
                <Image
                  objectFit="contain"
                  src={currentImage?.url}
                  maxHeight="100%"
                  maxWidth="100%"
                />
              </Box>

              <Flex
                direction="column"
                flex="1"
                pt="16px"
                pl="16px"
                backgroundColor="white"
              >
                <Flex align="center">
                  <Avatar name={currentImage?.createdBy.name} size="sm" />
                  <Text color="appSlate" ml="8px">
                    {currentImage?.createdBy?.name}
                  </Text>
                </Flex>
                <Text fontSize="16px" fontWeight="500" mt="12px">
                  “{currentImage?.prompt}”
                </Text>
                <Flex
                  direction="column"
                  mt="auto"
                  p="14px"
                  border="1px solid #e2e8f0"
                  borderRadius="8px"
                >
                  <Text color="appSlate">
                    {t('image_dimensions', 'Image dimensions')}
                  </Text>
                  <Text fontWeight="500">
                    {`${currentImage?.width} x ${currentImage?.height}`}
                  </Text>
                  <Text mt="20px" color="appSlate">
                    {t('created_on', 'Created on')}
                  </Text>
                  <Text fontWeight="500">
                    {moment(currentImage?.createdAt).format(
                      'ddd, MMMM Do YYYY, h:mm:ss A',
                    )}
                  </Text>
                </Flex>
                <Flex mt="16px" justify="end">
                  <PermissionGuard
                    permission={
                      PermissionsEnum.RESTRICTED_SELF_MEDIA_LIBRARY_MANAGE
                    }
                  >
                    {(hasPermission: boolean) =>
                      hasPermission ? (
                        <Button
                          colorScheme="blue"
                          onClick={() => {
                            onOpenSaveFileModal()
                          }}
                        >
                          {t('save_to_media_library', 'Save to media library')}
                        </Button>
                      ) : (
                        <></>
                      )
                    }
                  </PermissionGuard>
                  <Button
                    variant="link"
                    color="appSlate"
                    ml="14px"
                    fontWeight="normal"
                    leftIcon={
                      <HiOutlineDocumentDownload size="16px" color="#3b485e" />
                    }
                    onClick={() => {
                      const downloadURL = `${removeTrailingSlash(
                        user?.dashboardServerURL ?? '',
                      )}/api/media-library/${
                        user?.clientNumber
                      }/generate-image/${
                        currentImage?.imageId
                      }/download?fileName=image.jpg`

                      window.open(downloadURL)
                    }}
                  >
                    {t('download', 'Download')}
                  </Button>
                  <Menu>
                    <MenuButton
                      as={IconButton}
                      ml="14px"
                      color="appSlate"
                      aria-label="Options"
                      icon={<BsThreeDotsVertical />}
                      variant="link"
                      boxShadow="none"
                    />
                    <MenuList>
                      <MenuItem
                        icon={<MdContentCopy />}
                        onClick={() => {
                          onCopy()
                          toast({
                            description: t(
                              'copied_url_to_clipboard',
                              'Copied URL to clipboard',
                            ),
                            status: 'success',
                            isClosable: true,
                            duration: 5000,
                            position: 'top-right',
                            variant: 'outline',
                          })
                        }}
                      >
                        {t('copy_url', 'Copy URL')}
                      </MenuItem>
                      {user?.permissions.includes(
                        PermissionsEnum.RESTRICTED_SELF_MEDIA_LIBRARY_MANAGE,
                      ) &&
                        ((isRestrictedUser(user?.roles) &&
                          currentImage?.createdBy?.userId === user?.id) ||
                          !isRestrictedUser(user?.roles)) && (
                          <MenuItem
                            icon={<RiDeleteBinFill />}
                            color="appBrick"
                            onClick={onOpenDeleteGeneratedImageModal}
                          >
                            {t('delete', 'Delete')}
                          </MenuItem>
                        )}
                    </MenuList>
                  </Menu>
                </Flex>
              </Flex>
            </Flex>
          </ModalBody>
        </ModalContent>

        <Center
          position="absolute"
          right="3%"
          top="8%"
          transform="translateY(-25%)"
          zIndex="modal"
          width="40px"
          height="40px"
          borderRadius="50%"
        >
          <IconButton
            aria-label={t('close_modal', 'Close modal')}
            variant="link"
            color="white"
            fontWeight="600"
            icon={<IoIosClose size="32px" />}
            onClick={handleClose}
          />
        </Center>
        <Center
          position="absolute"
          left="3%"
          top="50%"
          transform="translateY(-50%)"
          zIndex="modal"
          width="40px"
          height="40px"
          borderRadius="50%"
          bg="rgba(255, 255, 255, 0.1)"
          backdropBlur="24px"
        >
          <IconButton
            aria-label={t('previous_image', 'Previous image')}
            variant="link"
            color="white"
            fontWeight="600"
            icon={<FiChevronLeft size="24px" />}
            onClick={handlePreviousImage}
            isDisabled={currentIndex === 0}
          />
        </Center>
        <Center
          position="absolute"
          right="3%"
          top="50%"
          transform="translateY(-50%)"
          zIndex="modal"
          width="40px"
          height="40px"
          borderRadius="50%"
          bg="rgba(255, 255, 255, 0.1)"
          backdropBlur="24px"
        >
          <IconButton
            aria-label={t('next_image', 'Next image')}
            variant="link"
            color="white"
            fontWeight="600"
            icon={<FiChevronRight size="24px" />}
            onClick={handleNextImage}
            isDisabled={currentIndex === generatedImages.length - 1}
          />
        </Center>
      </Modal>
      <DeleteGeneratedImageModal
        isOpen={isOpenDeleteGeneratedImageModal}
        onClose={onCloseDeleteGeneratedImageModal}
        imageIds={[generatedImages[currentIndex]?.imageId]}
        refetch={refetch}
      />
      <SaveNewFileModal
        isOpen={isOpenSaveFileModal}
        onClose={onCloseSaveFileModal}
        image={generatedImages[currentIndex]?.url}
      />
    </>
  )
}

export default ExpandImageModal
